import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { supabase } from '@/services/supabase-client.service';
import { login as loginService, logout as logoutService, mapSessionToAuthUser } from '@/services/auth.service';
import { logger } from '@/lib/logger';
import { syncRealtimeAuth } from '@/lib/supabase';
import type { AuthUser, LoginCredentials } from '@/types/auth';

interface AuthContextValue {
    user: AuthUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: (credentials: LoginCredentials) => Promise<void>;
    logout: () => Promise<void>;
    error: string | null;
    clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const loginInProgress = useRef(false);
    const isMountedRef = useRef(true);
    const authHydrationRequestId = useRef(0);

    const hydrateSession = useCallback(async (
        session: { access_token?: string; user?: { id: string; email?: string; user_metadata?: Record<string, unknown> } } | null,
    ) => {
        const requestId = authHydrationRequestId.current + 1;
        authHydrationRequestId.current = requestId;

        if (!session?.user) {
            if (!isMountedRef.current || requestId !== authHydrationRequestId.current) {
                return;
            }

            setUser(null);
            setIsLoading(false);
            return;
        }

        try {
            await syncRealtimeAuth(session.access_token ?? null);
            const authUser = await mapSessionToAuthUser(session.user);

            if (!isMountedRef.current || requestId !== authHydrationRequestId.current) {
                return;
            }

            setUser(authUser);
        } catch (err) {
            logger.warn('[Auth] Session-Hydration fehlgeschlagen:', err);

            if (!isMountedRef.current || requestId !== authHydrationRequestId.current) {
                return;
            }

            setUser(null);
        } finally {
            if (isMountedRef.current && requestId === authHydrationRequestId.current) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        isMountedRef.current = true;

        // Restore session on mount
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            logger.debug('[Auth] onAuthStateChange:', event);

            if (loginInProgress.current && event !== 'INITIAL_SESSION') {
                return;
            }

            await hydrateSession(session);
        });

        // Initial session check
        supabase.auth.getSession().then(({ data: { session } }) => {
            void hydrateSession(session);
        });

        return () => {
            isMountedRef.current = false;
            subscription.unsubscribe();
        };
    }, [hydrateSession]);

    const login = async (credentials: LoginCredentials) => {
        setError(null);
        setIsLoading(true);
        loginInProgress.current = true;
        try {
            const authUser = await loginService(credentials);
            if (!authUser) {
                throw new Error('Anmeldung fehlgeschlagen. Bitte Zugangsdaten prüfen.');
            }
            setUser(authUser);
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
            setError(msg);
            throw err;
        } finally {
            setIsLoading(false);
            loginInProgress.current = false;
        }
    };

    const logout = async () => {
        setIsLoading(true);
        try {
            await logoutService();
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    const clearError = () => setError(null);

    return (
        <AuthContext.Provider value={{ user, isLoading, isAuthenticated: !!user, login, logout, error, clearError }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
    return ctx;
}
